// src/resources/customerRfm/segments.ts
//
// Tipos e metadados dos Segmentos de valor (RFM), compartilhados pelo card do Painel 360
// (Painel360/LoyaltySection.tsx) e pela tela /customer-rfm. A régua mora no backend
// (pontuax-backend/src/services/CustomerRfm/rules.ts) e chega aqui em `cutoffs` — os textos de
// critério são montados a partir dela, então mudar um corte no backend muda o texto aqui.
//
// i18n: textos com número vão por __t com %{...} (o catálogo es tem o padrão com placeholder);
// rótulos fixos ficam em objeto { label } e o plugin de build embrulha sozinho.
import { __t } from '../../i18n/t';
import { COLORS } from '../dashboards/shared';

export type RfmSegment = 'champions' | 'loyal' | 'promising' | 'atRisk' | 'hibernating';

export const RFM_SEGMENT_ORDER: RfmSegment[] = ['champions', 'loyal', 'promising', 'atRisk', 'hibernating'];

export interface RfmCutoffs {
  /** Limite superior (inclusive) de dias para as notas de recência 5, 4, 3 e 2. */
  recencyDays: number[];
  /** Mínimo de abastecimentos em 12 meses para as notas de frequência 2, 3, 4 e 5. */
  frequencyMin: number[];
  windowMonths: number;
  compareDays: number;
  segmentRules: { segment: RfmSegment; minR: number | null; minF: number | null }[];
  fallbackSegment: RfmSegment;
}

/** Régua padrão (igual ao backend) — usada só enquanto o payload ainda não trouxe `cutoffs`. */
export const DEFAULT_RFM_CUTOFFS: RfmCutoffs = {
  recencyDays: [30, 60, 120, 240],
  frequencyMin: [2, 5, 12, 24],
  windowMonths: 12,
  compareDays: 30,
  segmentRules: [
    { segment: 'champions', minR: 4, minF: 4 },
    { segment: 'loyal', minR: 3, minF: 3 },
    { segment: 'promising', minR: 4, minF: null },
    { segment: 'atRisk', minR: null, minF: 3 },
  ],
  fallbackSegment: 'hibernating',
};

export interface RfmSegmentAggregate {
  segment: RfmSegment;
  customers: number;
  totalValue: number;
  /** Ausente em rollup antigo (antes da régua nova). */
  highValueCustomers?: number;
  /** null/ausente = sem comparação exata (filtro de posto ou rollup antigo). */
  previousCustomers?: number | null;
}

export interface RfmGridCell { r: number; f: number; segment: RfmSegment; customers: number; totalValue: number }
export interface RfmFlow { from: RfmSegment | null; to: RfmSegment; customers: number; totalValue: number }
export interface RfmLeft { from: RfmSegment; customers: number }

export interface RfmMeta {
  version: number;
  computedAt: string;
  windowStart: string;
  total: number;
  newCustomers: number;
  left: RfmLeft[];
  grid: RfmGridCell[];
  flows: RfmFlow[];
  cutoffs: RfmCutoffs;
}

export interface SegmentMeta {
  label: string;
  emoji: string;
  color: string;
  /** Subir é bom? (Campeões/Fiéis/Promissores sim; Em risco/Hibernando não.) */
  upIsGood: boolean;
  /** O que fazer com o grupo (playbook curto, 1-2 frases). */
  playbook: string;
  /** Uma ação concreta sugerida. */
  suggestion: string;
}

export const SEGMENT_META: Record<RfmSegment, SegmentMeta> = {
  champions: {
    label: 'Campeões', emoji: '🏆', color: COLORS.success, upIsGood: true,
    playbook: 'Seus melhores clientes: vêm sempre e voltaram há pouco. Eles já vêm sem desconto — reconheça, não subsidie.',
    suggestion: 'Etiquete como VIP e use em brinde exclusivo do Clube de Vantagens ou sorteio.',
  },
  loyal: {
    label: 'Fiéis', emoji: '💚', color: COLORS.blue, upIsGood: true,
    playbook: 'Vêm com regularidade, um degrau abaixo dos Campeões. O jogo aqui é aumentar a frequência.',
    suggestion: 'Pontuação em dobro por alguns dias ou meta de abastecimentos no mês.',
  },
  promising: {
    label: 'Promissores', emoji: '🌱', color: COLORS.purple, upIsGood: true,
    playbook: 'Chegaram agora ou vêm pouco, mas vieram recentemente. A 2ª e a 3ª visita decidem se ficam.',
    suggestion: 'Desconto na próxima visita ou notificação lembrando dos pontos.',
  },
  atRisk: {
    label: 'Em risco', emoji: '⚠️', color: COLORS.warning, upIsGood: false,
    playbook: 'Vinham com frequência e sumiram há mais de 4 meses. É onde mais dá retorno trazer de volta — comece pelos 💎.',
    suggestion: 'Etiquete e mande um desconto de volta (ou use a etiqueta na campanha de recuperação).',
  },
  hibernating: {
    label: 'Hibernando', emoji: '😴', color: COLORS.faint, upIsGood: false,
    playbook: 'Vieram poucas vezes e não voltam há mais de 2 meses. Vale um último resgate barato, sem gastar verba grande.',
    suggestion: 'Notificação simples ou desconto pequeno, só para quem tem celular cadastrado.',
  },
};

/** Rótulo das faixas de recência (linhas da grade, R5 → R1). */
export const recencyLabels = (c: RfmCutoffs = DEFAULT_RFM_CUTOFFS): Record<number, string> => {
  const [d5, d4, d3, d2] = c.recencyDays;
  return {
    5: __t('até %{d} dias', { d: d5 }),
    4: __t('%{a}–%{b} dias', { a: d5 + 1, b: d4 }),
    3: __t('%{a}–%{b} dias', { a: d4 + 1, b: d3 }),
    2: __t('%{a}–%{b} dias', { a: d3 + 1, b: d2 }),
    1: __t('mais de %{d} dias', { d: d2 }),
  };
};

/** Rótulo das faixas de frequência (colunas da grade, F1 → F5), em abastecimentos nos 12 meses. */
export const frequencyLabels = (c: RfmCutoffs = DEFAULT_RFM_CUTOFFS): Record<number, string> => {
  const [f2, f3, f4, f5] = c.frequencyMin;
  return {
    1: __t('1x'),
    2: __t('%{a}–%{b}x', { a: f2, b: f3 - 1 }),
    3: __t('%{a}–%{b}x', { a: f3, b: f4 - 1 }),
    4: __t('%{a}–%{b}x', { a: f4, b: f5 - 1 }),
    5: __t('%{a}x ou mais', { a: f5 }),
  };
};

const maxDaysForScore = (c: RfmCutoffs, minR: number): number => c.recencyDays[5 - minR];
const minFillinsForScore = (c: RfmCutoffs, minF: number): number => c.frequencyMin[minF - 2];

/**
 * Critério do segmento em uma frase, montado da régua (ex.: "Abasteceu nos últimos 60 dias e
 * veio 12x ou mais nos últimos 12 meses."). Usado em tooltip do card, cards da tela e manual.
 */
export const segmentCriteria = (segment: RfmSegment, c: RfmCutoffs = DEFAULT_RFM_CUTOFFS): string => {
  const rule = c.segmentRules.find(r => r.segment === segment);
  if (!rule) {
    // Fallback (Hibernando): não casou nenhuma regra acima → poucas vezes e sem vir recentemente.
    const promising = c.segmentRules.find(r => r.segment === 'promising');
    const days = promising?.minR ? maxDaysForScore(c, promising.minR) : c.recencyDays[1];
    const atRisk = c.segmentRules.find(r => r.segment === 'atRisk');
    const fillins = atRisk?.minF ? minFillinsForScore(c, atRisk.minF) - 1 : c.frequencyMin[1] - 1;
    return __t('Veio até %{n}x nos últimos 12 meses e não abastece há mais de %{d} dias.', { n: fillins, d: days });
  }
  if (rule.minR && rule.minF) {
    return __t('Abasteceu nos últimos %{d} dias e veio %{n}x ou mais nos últimos 12 meses.', {
      d: maxDaysForScore(c, rule.minR), n: minFillinsForScore(c, rule.minF),
    });
  }
  if (rule.minR) {
    return __t('Abasteceu nos últimos %{d} dias, mas ainda poucas vezes.', { d: maxDaysForScore(c, rule.minR) });
  }
  // Só frequência (Em risco): vinha bastante mas passou do corte de recência dos Fiéis.
  const loyal = c.segmentRules.find(r => r.segment === 'loyal');
  const days = loyal?.minR ? maxDaysForScore(c, loyal.minR) : c.recencyDays[2];
  return __t('Veio %{n}x ou mais nos últimos 12 meses, mas não abastece há mais de %{d} dias.', {
    n: minFillinsForScore(c, rule.minF!), d: days,
  });
};

/** Variação de 30 dias (null quando não há comparação exata). */
export const segmentDelta = (row?: RfmSegmentAggregate): number | null =>
  row && typeof row.previousCustomers === 'number' ? row.customers - row.previousCustomers : null;

/** Cor da variação: verde quando o movimento é bom para aquele segmento. */
export const deltaColor = (segment: RfmSegment, delta: number): string => {
  if (delta === 0) return COLORS.muted;
  const good = SEGMENT_META[segment].upIsGood ? delta > 0 : delta < 0;
  return good ? COLORS.success : COLORS.danger;
};

/** Um fluxo é "subida" quando vai para um segmento melhor (ordem de RFM_SEGMENT_ORDER). */
export const flowIsUp = (from: RfmSegment | null, to: RfmSegment): boolean =>
  from === null ? true : RFM_SEGMENT_ORDER.indexOf(to) < RFM_SEGMENT_ORDER.indexOf(from);

/** Nome sugerido para etiqueta do segmento: "RFM · Em risco · set/26". */
export const suggestedTagName = (segment: RfmSegment, now: Date = new Date()): string => {
  const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `RFM · ${__t(SEGMENT_META[segment].label)} · ${months[now.getMonth()]}/${String(now.getFullYear()).slice(2)}`;
};

/** Id da seção do RFM no manual (slug do heading "Segmentos de valor (RFM)"). */
export const RFM_MANUAL_SECTION = 'segmentos-de-valor-rfm';
